<template>
    <div class="emplacement-tree-selector" ref="root">
        <input type="hidden" :name="inputName" :value="selectedId">

        <!-- Champ cliquable -->
        <div class="ets-field"
             :class="{ 'is-invalid': invalid, 'is-open': dropdownOpen, 'has-value': !!selectedEmplacement }"
             @click="toggleDropdown">
            <span v-if="selectedEmplacement" class="ets-field-value">
                <i class="far fa-map-marker-alt text-primary mr-1"></i>
                <span class="ets-field-text">{{ selectedEmplacement.full_path || selectedEmplacement.name }}</span>
                <span v-if="selectedEmplacement.code" class="ets-field-code">{{ selectedEmplacement.code }}</span>
            </span>
            <span v-else class="ets-field-placeholder">
                {{ placeholder || 'Sélectionner une zone de stockage...' }}
            </span>
            <div class="ets-field-actions">
                <button v-if="selectedEmplacement" type="button" class="ets-clear-btn" @click.stop="clearSelection" title="Effacer">
                    <i class="fal fa-times"></i>
                </button>
                <i class="far fa-chevron-down ets-chevron" :class="{ 'rotated': dropdownOpen }"></i>
            </div>
        </div>

        <!-- Dropdown arborescent -->
        <transition name="ets-dropdown">
            <div v-if="dropdownOpen" class="ets-dropdown">
                <!-- Barre de recherche -->
                <div class="ets-search">
                    <i class="far fa-search ets-search-icon"></i>
                    <input type="text"
                           class="ets-search-input"
                           placeholder="Rechercher par nom ou code..."
                           v-model="searchQuery"
                           ref="searchInput">
                </div>

                <!-- Arbre -->
                <div class="ets-tree-list">
                    <div v-if="loading" class="ets-loading">
                        <i class="far fa-circle-notch fa-spin"></i>
                        <span>Chargement des zones de stockage...</span>
                    </div>
                    <div v-else-if="filteredFlatTree.length === 0" class="ets-empty">
                        <i class="far fa-map-marker-slash"></i>
                        <span>Aucune zone de stockage trouvée.</span>
                    </div>
                    <div v-else>
                        <div v-for="node in filteredFlatTree"
                             :key="node.id"
                             class="ets-node"
                             :class="{ 'selected': selectedId === node.id, 'disabled': isExcluded(node.id), 'has-children': node.children && node.children.length > 0 }"
                             @click="!isExcluded(node.id) && selectEmplacement(node)">
                            <span v-for="d in node.depth" :key="'g-' + d" class="ets-indent"></span>
                            <span class="ets-toggle" v-if="node.children && node.children.length > 0" @click.stop="toggleNode(node.id)">
                                <i class="far fa-chevron-right" :class="{ 'expanded': isExpanded(node.id) }"></i>
                            </span>
                            <span class="ets-toggle-placeholder" v-else></span>
                            <span class="ets-node-icon" :class="'ets-depth-' + Math.min(node.depth, 3)">
                                <i :class="node.depth === 0 ? 'far fa-warehouse' : (node.depth === 1 ? 'far fa-folder' : 'far fa-map-marker-alt')"></i>
                            </span>
                            <span class="ets-node-label" :class="'ets-label-depth-' + Math.min(node.depth, 2)">{{ node.name }}</span>
                            <span v-if="node.code" class="ets-node-code">{{ node.code }}</span>
                            <i v-if="selectedId === node.id" class="far fa-check ets-check"></i>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
export default {
    name: 'emplacement-tree-selector',
    props: {
        inputName: {
            type: String,
            default: 'emplacement_id'
        },
        initialEmplacement: {
            type: Object,
            default: null
        },
        placeholder: {
            type: String,
            default: ''
        },
        invalid: {
            type: Boolean,
            default: false
        },
        excludeId: {
            type: [Number, String],
            default: null
        },
        defaultEmplacement: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            selectedEmplacement: null,
            emplacements: [],
            loading: false,
            dropdownOpen: false,
            searchQuery: '',
            expandedNodeIds: new Set()
        };
    },
    computed: {
        selectedId() {
            return this.selectedEmplacement ? this.selectedEmplacement.id : '';
        },
        flatTree() {
            const map = {};
            this.emplacements.forEach(emp => {
                map[emp.id] = { ...emp, children: [] };
            });

            const roots = [];
            this.emplacements.forEach(emp => {
                const node = map[emp.id];
                if (node.parent_id && map[node.parent_id]) {
                    map[node.parent_id].children.push(node);
                } else {
                    roots.push(node);
                }
            });

            const flat = [];
            const traverse = (node, depth, path) => {
                node.depth = depth;
                node.path = path;
                flat.push(node);
                if (node.children && node.children.length > 0) {
                    node.children.forEach(child => {
                        traverse(child, depth + 1, [...path, node.id]);
                    });
                }
            };

            roots.forEach(root => {
                traverse(root, 0, []);
            });

            return flat;
        },
        filteredFlatTree() {
            const query = this.searchQuery.trim().toLowerCase();

            if (!query) {
                return this.flatTree.filter(node => {
                    return node.path.every(parentId => this.expandedNodeIds.has(parentId));
                });
            }

            const matchingNodeIds = new Set();
            this.flatTree.forEach(node => {
                if (node.name.toLowerCase().includes(query) || (node.code && node.code.toLowerCase().includes(query))) {
                    matchingNodeIds.add(node.id);
                }
            });

            const nodesToShow = new Set();
            this.flatTree.forEach(node => {
                const hasMatchingAncestor = node.path.some(parentId => matchingNodeIds.has(parentId));
                const isAncestorOfMatch = this.flatTree.some(other =>
                    matchingNodeIds.has(other.id) && other.path.includes(node.id)
                );

                if (matchingNodeIds.has(node.id) || hasMatchingAncestor || isAncestorOfMatch) {
                    nodesToShow.add(node.id);
                }
            });

            return this.flatTree.filter(node => nodesToShow.has(node.id));
        }
    },
    methods: {
        loadEmplacements() {
            this.loading = true;
            fetch('/api/emplacements/tree', {
                method: 'GET',
                ...this.getHttpJsonRequestHeaders()
            })
            .then(response => {
                if (!response.ok) throw new Error("Erreur de chargement");
                return response.json();
            })
            .then(data => {
                if (data.success) {
                    this.emplacements = data.emplacements;
                    this.expandFirstLevel();
                }
                this.loading = false;
            })
            .catch(error => {
                console.error("Erreur de chargement des zones de stockage", error);
                this.loading = false;
            });
        },
        expandFirstLevel() {
            this.emplacements.forEach(emp => {
                if (!emp.parent_id) {
                    this.expandedNodeIds.add(emp.id);
                }
            });
        },
        isExpanded(id) {
            return this.expandedNodeIds.has(id);
        },
        toggleNode(id) {
            if (this.expandedNodeIds.has(id)) {
                this.expandedNodeIds.delete(id);
            } else {
                this.expandedNodeIds.add(id);
            }
            this.expandedNodeIds = new Set(this.expandedNodeIds);
        },
        toggleDropdown() {
            this.dropdownOpen = !this.dropdownOpen;
            if (this.dropdownOpen && this.emplacements.length === 0) {
                this.loadEmplacements();
            }
            if (this.dropdownOpen) {
                this.$nextTick(() => {
                    if (this.$refs.searchInput) {
                        this.$refs.searchInput.focus();
                    }
                });
            }
        },
        isExcluded(id) {
            return this.excludeId !== null && parseInt(this.excludeId) === parseInt(id);
        },
        selectEmplacement(item) {
            this.selectedEmplacement = item;
            this.dropdownOpen = false;
            this.searchQuery = '';
            this.$emit('change', item);
        },
        clearSelection() {
            this.selectedEmplacement = null;
            this.$emit('change', null);
        },
        autoSelectDefault() {
            if (!this.selectedEmplacement && this.defaultEmplacement) {
                this.selectEmplacement(this.defaultEmplacement);
            }
        },
        handleClickOutside(e) {
            if (this.$refs.root && !this.$refs.root.contains(e.target)) {
                this.dropdownOpen = false;
            }
        }
    },
    mounted() {
        if (this.initialEmplacement) {
            this.selectedEmplacement = this.initialEmplacement;
        }
        document.addEventListener('click', this.handleClickOutside);
        this.$el.addEventListener('emplacement:autoselect', this.autoSelectDefault);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside);
        this.$el.removeEventListener('emplacement:autoselect', this.autoSelectDefault);
    }
};
</script>

<style scoped>
.emplacement-tree-selector {
    position: relative;
}

/* Champ de saisie */
.ets-field {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 38px;
    padding: 6px 12px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.ets-field:hover {
    border-color: #94a3b8;
}

.ets-field.is-open {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
}

.ets-field.is-invalid {
    border-color: #dc3545 !important;
}

.ets-field-value {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    line-height: 1.4;
    font-size: 13.5px;
    color: #1e293b;
}

.ets-field-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 500;
}

.ets-field-code {
    flex-shrink: 0;
    margin-left: 6px;
    padding: 1px 6px;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    font-size: 10.5px;
    font-family: 'Courier New', monospace;
    color: #64748b;
}

.ets-field-placeholder {
    color: #94a3b8;
    font-size: 13.5px;
}

.ets-field-actions {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
    margin-left: 8px;
}

.ets-clear-btn {
    border: none;
    background: transparent;
    color: #ef4444;
    cursor: pointer;
    padding: 2px;
    font-size: 12px;
    line-height: 1;
    opacity: 0.6;
    transition: opacity 0.15s ease;
}

.ets-clear-btn:hover {
    opacity: 1;
}

.ets-chevron {
    color: #94a3b8;
    font-size: 12px;
    transition: transform 0.2s ease;
}

.ets-chevron.rotated {
    transform: rotate(180deg);
}

/* Dropdown */
.ets-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 9999;
    background: #fff;
    border: 1px solid #3b82f6;
    border-top: none;
    border-radius: 0 0 8px 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    overflow: hidden;
}

.ets-dropdown-enter-active {
    transition: opacity 0.15s ease, transform 0.15s ease;
}

.ets-dropdown-enter {
    opacity: 0;
    transform: translateY(-4px);
}

/* Recherche */
.ets-search {
    display: flex;
    align-items: center;
    padding: 8px 10px;
    border-bottom: 1px solid #f1f5f9;
    background: #f8fafc;
}

.ets-search-icon {
    color: #94a3b8;
    font-size: 13px;
    margin-right: 8px;
}

.ets-search-input {
    flex: 1;
    border: none;
    background: transparent;
    font-size: 13px;
    color: #1e293b;
    outline: none;
}

.ets-search-input::placeholder {
    color: #94a3b8;
}

/* Liste arborescente */
.ets-tree-list {
    max-height: 300px;
    overflow-y: auto;
    padding: 4px 0;
}

.ets-tree-list::-webkit-scrollbar {
    width: 6px;
}

.ets-tree-list::-webkit-scrollbar-track {
    background: transparent;
}

.ets-tree-list::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
}

/* États de chargement / vide */
.ets-loading,
.ets-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 28px 16px;
    color: #94a3b8;
    font-size: 12.5px;
}

.ets-loading i {
    font-size: 20px;
}

.ets-empty i {
    font-size: 28px;
    opacity: 0.5;
}

/* Nœuds de l'arbre */
.ets-node {
    display: flex;
    align-items: center;
    padding: 5px 10px 5px 6px;
    cursor: pointer;
    white-space: nowrap;
    font-size: 13px;
    color: #334155;
    transition: background-color 0.1s ease;
}

.ets-node:hover {
    background-color: #f1f5f9;
}

.ets-node.selected {
    background-color: #eff6ff;
    color: #1e40af;
}

.ets-node.disabled {
    opacity: 0.35;
    cursor: not-allowed;
    pointer-events: none;
}

.ets-indent {
    display: inline-block;
    width: 18px;
    flex: 0 0 18px;
}

.ets-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    flex: 0 0 18px;
    color: #94a3b8;
    font-size: 10px;
    cursor: pointer;
    transition: color 0.15s ease;
}

.ets-toggle:hover {
    color: #475569;
}

.ets-toggle i.expanded {
    transform: rotate(90deg);
    transition: transform 0.15s ease;
}

.ets-toggle-placeholder {
    display: inline-block;
    width: 18px;
    flex: 0 0 18px;
}

.ets-node-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    flex: 0 0 22px;
    margin-right: 4px;
    font-size: 12px;
}

.ets-node-icon.ets-depth-0 {
    color: #3b82f6;
}

.ets-node-icon.ets-depth-1 {
    color: #6366f1;
}

.ets-node-icon.ets-depth-2 {
    color: #8b5cf6;
}

.ets-node-icon.ets-depth-3 {
    color: #94a3b8;
}

.ets-node-label {
    overflow: hidden;
    text-overflow: ellipsis;
}

.ets-node-label.ets-label-depth-0 {
    font-weight: 700;
    color: #0f172a;
}

.ets-node-label.ets-label-depth-1 {
    font-weight: 600;
    color: #1e293b;
}

.ets-node.selected .ets-node-label {
    color: #1e40af;
}

.ets-node-code {
    flex-shrink: 0;
    margin-left: 6px;
    padding: 0 5px;
    background: #f1f5f9;
    border-radius: 3px;
    font-size: 10px;
    font-family: 'Courier New', monospace;
    color: #64748b;
}

.ets-node.selected .ets-node-code {
    background: #dbeafe;
    color: #2563eb;
}

.ets-check {
    margin-left: auto;
    color: #3b82f6;
    font-size: 12px;
}

.is-invalid {
    border-color: #dc3545 !important;
}
</style>

<style>
.input-group > div:first-child .emplacement-tree-selector .ets-field {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
}
</style>
